import LocalesEditor from '@components/Customization/LocalesEditor';
import { languageName } from '@lib/customization/languages';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { Box, Button, CardContent, Chip, Paper, Stack, Typography } from '@mui/material';
import { localeCodeChipStyles, primaryChipStyles } from '@pages/CustomPoiTypes.styles';
import { usePermission } from '@store/hooks';
import { type FC, useState } from 'react';
import { GlobeIcon } from 'react-line-awesome';
import { CardDescription, CardHeader, CardTitle } from './ServerConfig.styled';

/**
 * The languages customization content is written in: what POI types, report designs and the
 * report brand offer a text in, and which of them stands in for an empty one. They are the
 * server's and no one customization's, which is why they are set here; the POI types page
 * had the button while POI types were the only customization there was.
 *
 * The card only shows them. They are changed in the drawer its button opens, which saves by
 * itself, apart from the page's Save.
 */
const CustomizationLocalesCard: FC = () => {
  const { t, currentLanguage } = useLocalization();
  const { can } = usePermission();
  const { primary, enabled } = useCustomizationLocales();
  const [editing, setEditing] = useState(false);

  return (
    <Paper sx={{ mb: 2 }}>
      <CardHeader>
        <Box>
          <CardTitle variant="h6">{t('ServerConfig_CustomizationLocales')}</CardTitle>
          <CardDescription variant="body2">{t('ServerConfig_CustomizationLocales_Description')}</CardDescription>
        </Box>
        {can('server_config:put') && (
          <Button
            variant="outlined"
            startIcon={<GlobeIcon />}
            onClick={() => setEditing(true)}
            sx={{ flexShrink: 0, ml: 2 }}
          >
            {t('Customization_EditLocales')}
          </Button>
        )}
      </CardHeader>
      <CardContent style={{ padding: '24px' }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          {enabled.map((code) => (
            <Stack
              key={code}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.5, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
            >
              <Chip size="small" label={code.toUpperCase()} sx={localeCodeChipStyles} />
              <Typography variant="body2">{languageName(code, currentLanguage)}</Typography>
              {code === primary && (
                <Chip size="small" color="primary" label={t('Customization_PrimaryLocale')} sx={primaryChipStyles} />
              )}
            </Stack>
          ))}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {t('Customization_PrimaryHint')}
        </Typography>
      </CardContent>

      <LocalesEditor open={editing} onClose={() => setEditing(false)} />
    </Paper>
  );
};

export default CustomizationLocalesCard;
